/* eslint-disable complexity */
import { memo, useMemo, useState } from "react";
import { PageTitle } from "~/common/components/PageTitle/v2/PageTitle";

import { editMyAccount } from "~/features/myAccount/profile/api/editMyAccount";
import { MarketingConsent } from "~/features/marketingConsent/components/MarketingConsent";
import { Controller, useForm } from "react-hook-form";
import { SimpleSelect } from "~/common/components/Select/SimpleSelect";
import { ChangePasswordSection } from "../ChangePasswordSection";
import { type MyAccountData, type MyAccountValues } from "../../myAccountTypes";
import { Row } from "~/common/components/Layout";
import ResetPasswordPopup from "~/features/auth/login/components/ResetPasswordPopup";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { HOMEPAGE } from "~/utils/url/staticUrls";
import { AFFILIATE_FORM_KEY, AffiliateFormSection } from "~/features/affiliate/components/AffiliateFormSection";
import { clearSavedSalesPartnerCookieOnAccountUncheck } from "~/domains/affiliate/utils/utilsAccount";
import {
  trackMyAccountSavePartner,
  trackMyAccountSaveSubsidiary,
  trackMyAccountUnsavePartner,
} from "~/app/analytics/ga/affiliate";
import { uniqueObjects } from "~/common/utils/misc";
import { PHONE_REGEXP, ZIPCODE_MAX_LENGTH, ZIPCODE_MIN_LENGTH, ZIPCODE_REGEXP } from "~/common/utils/validations";
import { MandatoryMessage } from "../common";
import { isMarketingConsentEnabled } from "~/utils/features";
import { makeSelectRef4RHF, useConfirmBeforeUnload } from "~/features/myAccount/profile/formUtils";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { FormSection } from "~/pages/MyAccountPage/MyAccountPage";
import { useTranslation } from "react-i18next";
import { SimpleAutocomplete } from "~/common/components/Select/SimpleAutocompleteSelect";
import { TrainingConsent, FlagBadge } from "~/features/myAccount/profile/components/TrainingConsent";
import { Box, Button, Stack, TextField, styled } from "~/common/mui";
import { useMyAccountDropdownValues } from "~/features/registration/hooks/useMyAccountDropdownValues";
import { ModalConfirmation } from "~/features/myAccount/profile/components/ModalConfirmation";
import { MarketingCloudProfileCenterIframe } from "~/features/myAccount/profile/components/MarketingCloudProfileCenterIframe/MarketingCloudProfileCenterIframe";
import { MyAccountParts } from "~/features/myAccount/profile/components/MyAccountParts";
import { setUserStatus } from "~/features/lms/utils/lmsUtils";
import { useSelector } from "react-redux";
import { selectSavedAffiliate, selectVisitedAffiliate } from "~/domains/affiliate/redux/selectors";
import { Alert } from "~/common/mui/Alert/Alert";
import { Snackbar, SnackbarCloseButton } from "~/common/mui/Snackbar/Snackbar";
import DeleteAccount from "../DeleteAccount/DeleteAccount";
import TrainingConsentInfo from "~/features/myAccount/profile/components/TrainingConsentInfo/TrainingConsentInfo";

interface Props {
  data: MyAccountData;
}

const MyAccountRegistered = (props: Props) => {
  const { data } = props;
  const { t } = useTranslation();
  const isMinDesktop = useIsMinDesktopSize();

  const [newsletterOne, setNewsletterOne] = useState<boolean>(data.newsletterOne);
  const [showInfo, setShowInfo] = useState<boolean>(false);
  const [showBEerror, setShowBEerror] = useState<boolean>(false);
  const [isEnableFieldsTraining, setIsEnableFieldsTraining] = useState<boolean>(data.inputFieldValues.lmsRequest);
  const { val: modalCategoryOpen, setTrue: openModalCategory, setFalse: closeModalCategory } = useBooleanState(false);
  const [trainingCheckboxTouched, setTrainingCheckboxTouched] = useState(false);
  const { val: trainingModal, setTrue: showTrainingModal, setFalse: hideTrainingModal } = useBooleanState(false);

  const dropdownOptions = useMyAccountDropdownValues(data);
  const [isSubmitted, setSubmitted] = useState(data.newsletterOne);

  const { register, handleSubmit, control, trigger, watch, setError, setValue, formState, reset, getValues } =
    useForm<MyAccountValues>({
      defaultValues: {
        ...data.inputFieldValues,
        [AFFILIATE_FORM_KEY]: { ...(data.inputFieldValues[AFFILIATE_FORM_KEY] ?? { id: "", subsidiary: "" }) },
      },
      reValidateMode: "onBlur",
    });

  const initialId = data.inputFieldValues[AFFILIATE_FORM_KEY]?.id ?? "";
  const initialSubsidiary = data.inputFieldValues[AFFILIATE_FORM_KEY]?.subsidiary ?? "";
  const haveInitialCategory = !!data.inputFieldValues.customerCategory;
  const customerCategoryCode = watch("customerCategory");
  const customerCategoryLabel = data.customerCategories.find((item) => item.code === customerCategoryCode)?.name ?? "";
  const onSubmit = (data: MyAccountValues, confirmed: boolean) => {
    if (!haveInitialCategory && !!customerCategoryCode && !confirmed && isEnableFieldsTraining) {
      return openModalCategory();
    } else {
      closeModalCategory();
    }
    const currentId = data[AFFILIATE_FORM_KEY]?.id ?? "";

    if (initialId !== currentId) {
      let fun;
      let idToFind: string;
      if (currentId) {
        fun = trackMyAccountSavePartner;
        idToFind = currentId;
      } else {
        fun = trackMyAccountUnsavePartner;
        idToFind = initialId;
      }

      const salesPartnerName = affiliates.find((affiliate) => affiliate.affiliateId === idToFind)?.bannerName ?? "";
      fun(salesPartnerName);
    }

    const currentSubsidiary = data[AFFILIATE_FORM_KEY]?.subsidiary ?? "";
    if (currentSubsidiary !== "" && currentSubsidiary !== initialSubsidiary) {
      const affiliateData = affiliates.find((affiliate) => affiliate.affiliateId === currentId);
      const subsidiaryData = affiliateData?.subsidiaries?.find((subsidiary) => subsidiary.value === currentSubsidiary);

      if (subsidiaryData) {
        const address = [
          subsidiaryData.address?.line1,
          subsidiaryData.address?.line2,
          subsidiaryData.address?.town,
          subsidiaryData.address?.country.name,
        ]
          .filter(Boolean)
          .join(" ");
        trackMyAccountSaveSubsidiary(subsidiaryData.label ?? "", subsidiaryData.address?.postalCode ?? "", address);
      }
    }

    setSubmitted(newsletterOne);
    if (trainingCheckboxTouched) {
      showTrainingModal();
      setTrainingCheckboxTouched(false);
    } else {
      setShowInfo(true);
    }
    editMyAccount({
      ...data,
      newsletterOne: newsletterOne,
      isEnableFieldsTraining: isEnableFieldsTraining,
      isPlusUser: true,
    }).then((response) => {
      if (response.oldPasswordWrong) {
        setError(
          "oldPassword",
          {
            type: "wrongPassword",
          },
          { shouldFocus: true },
        );
      } else {
        if (response.success) {
          reset(getValues());

          if (trainingCheckboxTouched) {
            showTrainingModal();
            setTrainingCheckboxTouched(false);
          } else {
            setShowInfo(true);
          }
          setUserStatus(response.lmsState);
        } else {
          setShowBEerror(true);
        }
      }
    });

    if (!data.affiliate?.id) {
      clearSavedSalesPartnerCookieOnAccountUncheck();
    }
  };

  const affiliates = useMemo(() => uniqueObjects(data.affiliates, (affiliate) => affiliate.affiliateId), []);

  const getLabel = (name: string, required?: boolean) => t(`page.completeRegistration.${name}`) + (required ? "*" : "");
  const pleaseEnterError = (fieldName: string) => t("page.profile.pleaseEnter." + fieldName);
  const { val: resetPopupOpen, setFalse: closeReset } = useBooleanState(false);

  const hideMarketingConsent = isSubmitted && newsletterOne;

  const visitedAffiliate = useSelector(selectVisitedAffiliate);
  const savedAffiliate = useSelector(selectSavedAffiliate);

  const hasSomeAffiliate = visitedAffiliate || savedAffiliate;

  const handleOnCheck = (val: boolean) => {
    setIsEnableFieldsTraining(val);
    setTrainingCheckboxTouched(val);
  };

  useConfirmBeforeUnload(formState);

  return (
    <form onSubmit={handleSubmit((data) => onSubmit(data, false))} id={"complete-form"}>
      <TrainingConsentInfo modal={trainingModal} closeModal={hideTrainingModal} />
      <PageTitle>{t("myAccount.title")}</PageTitle>
      <MyAccountRoot className="scha__my-account">
        <MyAccountForm className={"scha__my-account__form"}>
          <div className={"scha__my-account__form__column"}>
            <FormSection title={t("myAccount.name.title")}>
              <Controller
                name={"title"}
                control={control}
                rules={{ required: false }}
                render={({ field: { value, onChange, onBlur, ref }, fieldState: { error } }) => (
                  <SimpleSelect
                    values={dropdownOptions.titleOptions}
                    label={getLabel("customerTitle", false)}
                    error={!!error}
                    fullWidth={true}
                    onBlur={onBlur}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    ref={ref}
                  />
                )}
              />

              <Controller
                name={"salutation"}
                control={control}
                render={({ field: { value, onChange, onBlur, ref }, fieldState: { error } }) => (
                  <SimpleSelect
                    values={dropdownOptions.salutationOptions}
                    label={getLabel("salutation", true)}
                    error={!!error}
                    fullWidth={true}
                    onBlur={onBlur}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    ref={makeSelectRef4RHF(ref)}
                    helperText={error?.message ?? ""}
                  />
                )}
                rules={{
                  required: pleaseEnterError("salutation"),
                }}
              />

              <Controller
                name="firstName"
                control={control}
                render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                  <TextField
                    fullWidth
                    label={getLabel("firstName", true)}
                    inputProps={{ "data-test-id": "complete-registration-firstName" }}
                    inputRef={ref}
                    error={!!error}
                    helperText={error?.message ?? true}
                    {...inputProps}
                  />
                )}
                rules={{
                  required: pleaseEnterError("firstName"),
                }}
              />

              <Controller
                name="lastName"
                control={control}
                render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                  <TextField
                    fullWidth
                    label={getLabel("lastName", true)}
                    inputProps={{ "data-test-id": "complete-registration-lastName" }}
                    inputRef={ref}
                    error={!!error}
                    helperText={error?.message ?? true}
                    {...inputProps}
                  />
                )}
                rules={{
                  required: pleaseEnterError("lastName"),
                }}
              />

              <MyAccountParts.EmailParameter email={data.inputFieldValues.email} />
              <MyAccountParts.ChangeEmailLink href={data.profileCenterEmailLink} />
            </FormSection>

            {((affiliates.length > 0 && hasSomeAffiliate) ||
              !!data.inputFieldValues[AFFILIATE_FORM_KEY]?.subsidiary) && (
              <AffiliateFormSection
                control={control}
                register={register}
                setValue={setValue}
                watch={watch}
                affiliates={affiliates}
                initialAffiliate={data.inputFieldValues[AFFILIATE_FORM_KEY]?.subsidiary ?? undefined}
              />
            )}
            <Box sx={{ mt: "17px !important" }}>
              <ChangePasswordSection
                control={control}
              values={{
                oldPassword: watch("oldPassword"),
                newPassword: watch("newPassword"),
                newPasswordConfirmation: watch("newPasswordConfirmation"),
              }}
              triggerValidation={trigger}
            />
            </Box>
          </div>

          <div className={"scha__my-account__form__column"}>
            <FormSection title={t("myAccount.company.title")}>
              <Controller
                name={"company"}
                control={control}
                rules={{ required: pleaseEnterError("companyName") }}
                render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                  <TextField
                    fullWidth
                    label={getLabel("companyName", true)}
                    inputProps={{ "data-test-id": "complete-registration-company" }}
                    error={!!error}
                    helperText={error?.message ?? true}
                    inputRef={ref}
                    {...inputProps}
                  />
                )}
              />
              <Controller
                render={({ field: { value, onChange, onBlur, ref }, fieldState: { error } }) => (
                  <SimpleSelect
                    values={dropdownOptions.industryOptions}
                    label={getLabel("industrySector", true)}
                    fullWidth={true}
                    data-test-id={"complete-registration-industry"}
                    error={!!error}
                    onBlur={onBlur}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    helperText={error?.message ?? ""}
                    ref={makeSelectRef4RHF(ref)}
                  />
                )}
                name={"industrySector"}
                control={control}
                rules={{
                  required: pleaseEnterError("industrySector"),
                }}
              />

              <Controller
                render={({ field: { value, onChange, onBlur } }) => (
                  <SimpleSelect
                    values={dropdownOptions.functionalAreaOptions}
                    label={getLabel("functionalArea")}
                    fullWidth={true}
                    data-test-id={"complete-registration-functional-area"}
                    onBlur={onBlur}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                  />
                )}
                name={"functionalArea"}
                control={control}
              />
              <Controller
                render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                  <SimpleSelect
                    values={dropdownOptions.jobTitleOptions}
                    label={getLabel("jobTitle", false)}
                    fullWidth={true}
                    data-test-id={"complete-registration-job-title"}
                    onBlur={onBlur}
                    value={value}
                    error={!!error}
                    onChange={(e) => onChange(e.target.value)}
                    helperText=""
                  />
                )}
                name={"jobTitle"}
                control={control}
              />
              <Controller
                name={"address"}
                control={control}
                rules={{
                  required: isEnableFieldsTraining,
                }}
                render={({ field: { ref, ...inputProps } }) => (
                  <TextField
                    fullWidth
                    label={getLabel("address", isEnableFieldsTraining)}
                    inputProps={{ "data-test-id": "complete-registration-address" }}
                    autoComplete={"address-line1"}
                    inputRef={ref}
                    helperText=""
                    {...inputProps}
                  />
                )}
              />
              <Controller
                name={"addressAdditional"}
                control={control}
                render={({ field: { ref, ...inputProps } }) => (
                  <TextField
                    fullWidth
                    label={getLabel("addressAdditional")}
                    inputProps={{ "data-test-id": "complete-registration-addressAdditional" }}
                    autoComplete={"address-line2"}
                    inputRef={ref}
                    helperText=""
                    {...inputProps}
                  />
                )}
              />
              <div>
                <div className="scha__my-account__form__inputs__zip-city">
                  <div className={"scha__my-account__form__inputs__zip-city__zip"}>
                    <Controller
                      name={"zipCode"}
                      control={control}
                      rules={{
                        minLength: {
                          value: ZIPCODE_MIN_LENGTH,
                          message: t("checkoutPage.addressModal.error.isTooShortPart", {
                            fieldname: t(`checkoutPage.addressModal.input.label.zipCode`),
                          }),
                        },
                        maxLength: {
                          value: ZIPCODE_MAX_LENGTH,
                          message: t("checkoutPage.addressModal.error.isTooLongPart", {
                            fieldname: t(`checkoutPage.addressModal.input.label.zipCode`),
                          }),
                        },
                        pattern: {
                          value: ZIPCODE_REGEXP,
                          message: t("checkoutPage.addressModal.error.forbidenCharactersPart", {
                            fieldname: t(`checkoutPage.addressModal.input.label.zipCode`),
                          }),
                        },
                        required: isEnableFieldsTraining,
                      }}
                      render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                        <TextField
                          fullWidth
                          label={getLabel("zipCode", isEnableFieldsTraining)}
                          inputProps={{
                            "data-test-id": "complete-registration-zipCode",
                            onBeforeInput: (e) => {
                              const event = e as unknown as InputEvent;
                              const val = (e.target as HTMLInputElement).value + event.data;
                              if (!ZIPCODE_REGEXP.test(val.trim())) {
                                e.preventDefault();
                                e.stopPropagation();
                              }
                            },
                          }}
                          error={!!error}
                          helperText={error?.message}
                          autoComplete={"postal-code"}
                          inputRef={ref}
                          {...inputProps}
                        />
                      )}
                    />
                  </div>

                  <div className="scha__my-account__form__inputs__zip-city__city">
                    <Controller
                      name={"city"}
                      control={control}
                      rules={{
                        required: isEnableFieldsTraining,
                      }}
                      render={({ field: { ref, ...inputProps } }) => (
                        <TextField
                          fullWidth
                          label={getLabel("city", isEnableFieldsTraining)}
                          inputProps={{ "data-test-id": "complete-registration-city" }}
                          autoComplete={"city"}
                          inputRef={ref}
                          {...inputProps}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>

              <Controller
                name={"country"}
                control={control}
                render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                  <SimpleAutocomplete
                    label={getLabel("country", true)}
                    data-test-id={"complete-registration-country"}
                    values={dropdownOptions.countryOptions}
                    fullWidth={true}
                    error={!!error}
                    helperText={error?.message ?? " "}
                    onBlur={onBlur}
                    value={value}
                    onChange={onChange}
                  />
                )}
                rules={{ required: true }}
              />

              <Controller
                name={"phone"}
                control={control}
                render={({ field: { ref, ...inputProps } }) => (
                  <TextField
                    fullWidth
                    label={getLabel("phone")}
                    helperText={getLabel("examplePhone")}
                    inputProps={{
                      "data-test-id": "complete-registration-phone",
                      onBeforeInput: (e) => {
                        const event = e as unknown as InputEvent;
                        const val = (e.target as HTMLInputElement).value + event.data;
                        if (!PHONE_REGEXP.test(val.trim())) {
                          e.preventDefault();
                          e.stopPropagation();
                        }
                      },
                    }}
                    autoComplete={"tel"}
                    inputRef={ref}
                    {...inputProps}
                  />
                )}
              />

              {isEnableFieldsTraining && (
                <Controller
                  name={"vat"}
                  control={control}
                  render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                    <TextField
                      fullWidth
                      label={getLabel("vat")}
                      helperText={getLabel("exampleVat")}
                      inputProps={{ "data-test-id": "complete-registration-vat" }}
                      error={!!error}
                      inputRef={ref}
                      {...inputProps}
                    />
                  )}
                />
              )}

              <Alert className="mt-16" severity={"info"}>
                {t("myAccount.info")}
              </Alert>
            </FormSection>
          </div>

          <div className={"scha__my-account__form__column"}>
            {isMinDesktop && (
              <div className="d-flex">
                <TrainingTitle className="mr-16">
                  {t("page.registerForFree.baner.trainingCampus")}
                </TrainingTitle>
                <FlagBadge>{t("myAccount.training.flag.label")}</FlagBadge>
              </div>
            )}

            <TrainingConsent
              className={"scha__my-account__training-campus"}
              checked={isEnableFieldsTraining}
              onCheck={handleOnCheck}
              disabled={data.inputFieldValues.lmsRequest}
            />

            {isEnableFieldsTraining && (
              <>
                <p className="my-30 title-medium">{t("myAccount.training.welcome")}</p>
                <Stack gap={2}>
                  <Controller
                    rules={{
                      required: isEnableFieldsTraining,
                    }}
                    render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                      <SimpleSelect
                        values={dropdownOptions.languageOptions}
                        label={getLabel("language", isEnableFieldsTraining)}
                        fullWidth={true}
                        error={!!error}
                        data-test-id={"complete-registration-language"}
                        onBlur={onBlur}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                      />
                    )}
                    name={"language"}
                    control={control}
                  />

                  <Controller
                    rules={{
                      required: isEnableFieldsTraining,
                    }}
                    render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                      <SimpleSelect
                        values={dropdownOptions.customerCategoryOptions}
                        label={getLabel("customerCategory", isEnableFieldsTraining)}
                        fullWidth={true}
                        error={!!error}
                        data-test-id={"complete-registration-customer-category"}
                        onBlur={onBlur}
                        value={value}
                        disabled={haveInitialCategory}
                        onChange={(e) => onChange(e.target.value)}
                      />
                    )}
                    name={"customerCategory"}
                    control={control}
                  />
                  <Controller
                    rules={{
                      required: isEnableFieldsTraining,
                    }}
                    render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                      <SimpleSelect
                        values={dropdownOptions.knowledgeAreaOptions}
                        label={getLabel("knowledgeArea", isEnableFieldsTraining)}
                        fullWidth={true}
                        error={!!error}
                        data-test-id={"complete-registration-knowledge-area"}
                        onBlur={onBlur}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                      />
                    )}
                    name={"knowledgeArea"}
                    control={control}
                  />
                  {!haveInitialCategory && (
                    <Alert severity={"info"} className={"mb-2"}>
                      {t("myAccount.customerCategory.info.text")}
                    </Alert>
                  )}
                </Stack>
              </>
            )}
          </div>
        </MyAccountForm>

        <MandatoryMessage />

        {isMarketingConsentEnabled() && !hideMarketingConsent && (
          <MarketingConsent selected={newsletterOne} setSelected={(newVal: boolean) => setNewsletterOne(newVal)} />
        )}

        {/* BUTTONS*/}
        <div className="scha__my-account__form__buttons-area">
          <Button variant={"text"} href={HOMEPAGE} data-test-id={"my-account-button-cancel"}>
            {getLabel("cancel")}
          </Button>
          <Button data-test-id={"test-id-0"} onClick={handleSubmit((data) => onSubmit(data, false))}>
            {t("text.company.save.button")}
          </Button>
        </div>

        {isMarketingConsentEnabled() && hideMarketingConsent && (
          <MarketingCloudProfileCenterIframe
            className={"scha__my-account__mcProfile-iframe"}
            url={data.profileCenterLink}
          />
        )}

        <DeleteAccount />

        <Snackbar
          open={showInfo}
          onClose={() => setShowInfo(false)}
          message={t("myAccount.successInfo")}
          action={<SnackbarCloseButton onClick={() => setShowInfo(false)} />}
        />
        <Snackbar
          open={showBEerror}
          onClose={() => setShowBEerror(false)}
          message={t("myAccount.dataLoadError")}
          action={<SnackbarCloseButton onClick={() => setShowBEerror(false)} />}
        />

        {resetPopupOpen && (
          <ResetPasswordPopup initialEmail={data.inputFieldValues.email} close={closeReset} disableEdit={true} />
        )}
      </MyAccountRoot>

      {modalCategoryOpen && (
        <ModalConfirmation
          onClose={closeModalCategory}
          onSubmit={handleSubmit((data) => onSubmit(data, true))}
          selectedValue={customerCategoryLabel}
          inputField={t(`page.completeRegistration.customerCategory`)}
        />
      )}
    </form>
  );
};

const MyAccountRoot = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  [theme.breakpoints.up("tablet")]: {
    gap: theme.spacing(4),
  },
  "& .scha__my-account__form__column": {
    display: "flex",
    flexDirection: "column",
    flexWrap: "wrap",
    width: "100%",
    flex: "none",
    [theme.breakpoints.up("desktop-s")]: {
      flex: 1,
    },
  },
  "& .scha__my-account__training-campus": {
    marginTop: theme.spacing(1),
    [theme.breakpoints.up("tablet")]: {
      marginTop: theme.spacing(2),
    },
    [theme.breakpoints.up("desktop-s")]: {
      marginTop: 0,
    },
  },
  "& .scha__my-account__form__inputs__zip-city": {
    width: "100%",
    display: "flex",
    "&__zip": {
      width: "31%",
      paddingRight: theme.spacing(2),
    },
    "&__city": {
      width: "69%",
      paddingLeft: theme.spacing(2),
    },
  },
  "& .scha__my-account__form__terms-training": {
    marginTop: theme.spacing(4),
  },
  "& .scha__my-account__form__buttons-area": {
    width: "100%",
    display: "flex",
    flexFlow: "row-reverse wrap-reverse",
    gap: theme.spacing(2),
    "& > *": {
      width: "100%",
    },
    [theme.breakpoints.up("tablet")]: {
      justifyContent: "flex-end",
      flexFlow: "row",
      "& > *": {
        width: "unset",
      },
    },
  },
  "& .scha__my-account__mcProfile-iframe": {
    marginTop: theme.spacing(4),
  },
}));

const TrainingTitle = styled("h3")(({ theme }) => ({
  ...theme.typography.titleLarge,
  marginTop: 0,
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("tablet")]: {
    ...theme.typography.headlineSmall,
    marginBottom: theme.spacing(3),
  },
}));

const MyAccountForm = styled(Row)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(3),
  "&&": {
    margin: 0,
  },
  [theme.breakpoints.up("tablet")]: {
    gap: theme.spacing(4),
  },
}));

export default memo(MyAccountRegistered);
